import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { CategoryListItemDto } from 'libs/end-to-end/src/rubrics';
import { RubricRecordDto } from 'libs/end-to-end/src/rubrics';
import { CreateRubricForWrite } from 'libs/end-to-end/src/rubrics';
import { UpdateRubricForWrite } from 'libs/end-to-end/src/rubrics';
import { TagRecordDto } from 'libs/end-to-end/src/rubrics';
import { RoleListItemDto } from 'libs/end-to-end/src/rubrics';
import { RubricDetailsDto } from 'libs/end-to-end/src/rubrics';
import { UpdateRubricStatusBodySchema } from 'libs/end-to-end/src/rubrics';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { isApiError } from 'libs/end-to-end/src/types/errors/api-error';
import { z } from 'zod';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';

export const getRubricsBaseUrl = ({
  organizationId,
  databaseId,
  databaseRevisionId,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
}) => {
  return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${databaseRevisionId}/rubrics`;
};

export const getRubricRolesUrl = ({
  organizationId,
  databaseId,
  databaseRevisionId,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
}) => {
  return `${getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  })}/roles`;
};

export const clientUpdateRubricStatus = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  status,
  rubricId,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: () => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  status: RubricStatus;
  rubricId: string;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/${rubricId}/status`;
  const body: UpdateRubricStatusBodySchema = {
    status,
  };

  userAuthenticatedApiCall({
    url,
    responseSchema: RubricRecordDto,
    requestOptions: {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess();
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientGetRubricDetails = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  rubricId,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (rubric: RubricDetailsDto) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  rubricId: string;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/${rubricId}`;

  userAuthenticatedApiCall({
    url,
    responseSchema: RubricDetailsDto,
    requestOptions: {
      method: 'GET',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientGetRubricRoles = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (roles: RoleListItemDto[]) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/roles`;

  userAuthenticatedApiCall({
    url,
    responseSchema: z.array(RoleListItemDto),
    requestOptions: {
      method: 'GET',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientGetRubricCategories = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  gradingType,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (roles: CategoryListItemDto[]) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  gradingType: RubricGradingType;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/categories?gradingType=${gradingType}`;

  userAuthenticatedApiCall({
    url,
    responseSchema: z.array(CategoryListItemDto),
    requestOptions: {
      method: 'GET',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientGetRubricTags = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (roles: TagRecordDto[]) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/tags`;

  userAuthenticatedApiCall({
    url,
    responseSchema: z.array(TagRecordDto),
    requestOptions: {
      method: 'GET',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientCreateRubric = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  rubric,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (roles: RubricDetailsDto) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  rubric: CreateRubricForWrite;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}`;

  userAuthenticatedApiCall({
    url,
    responseSchema: RubricDetailsDto,
    requestOptions: {
      method: 'POST',
      body: JSON.stringify(rubric),
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientUpdateRubric = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  rubricId,
  rubric,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (newDto: RubricDetailsDto) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  rubricId: string;
  rubric: UpdateRubricForWrite;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/${rubricId}`;

  userAuthenticatedApiCall({
    url,
    responseSchema: RubricDetailsDto,
    requestOptions: {
      method: 'PUT',
      body: JSON.stringify(rubric),
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientDeleteRubric = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
  rubricId,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: () => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
  rubricId: string;
}) => {
  if (!isAlive()) {
    return;
  }

  const baseUrl = getRubricsBaseUrl({
    organizationId,
    databaseId,
    databaseRevisionId,
  });
  const url = `${baseUrl}/${rubricId}`;

  userAuthenticatedApiCall({
    url,
    responseSchema: EmptyObject,
    requestOptions: {
      method: 'DELETE',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess();
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};

export const clientGetIntegrationDataSets = ({
  organizationId,
  userAuthenticatedApiCall,
  onSuccess,
  onError,
  isAlive,
}: {
  organizationId: string;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  onSuccess: (roles: DataSet[]) => void;
  onError: (error: string | null) => void;
  isAlive: IsAliveFn;
}) => {
  if (!isAlive()) {
    return;
  }

  const url = `${getApiBaseUrl()}/organizations/${organizationId}/data-sets`;

  userAuthenticatedApiCall({
    url,
    responseSchema: z.array(DataSet),
    requestOptions: {
      method: 'GET',
    },
  })
    .then((result) => {
      if (!isAlive()) {
        return;
      }

      if (result === null) {
        onError(null);
        return;
      }

      if (result.resultType === 'err') {
        onError(isApiError(result.error) ? result.error.errorReason : null);
        return;
      }

      onSuccess(result.value);
    })
    .catch((error) => {
      if (!isAlive()) {
        return;
      }

      onError(isApiError(error) ? error.errorReason : null);
    });
};
